// Helpers de formatage partagés.

/**
 * Formate une durée relative à maintenant en format compact français.
 * Exemple : `45 s`, `23 min`, `2 h 14 min`, `3 j 5 h`, `2 mois`.
 * Retourne une chaîne vide si la date est invalide ou absente.
 */
export function fmtRelativeFromNow(d) {
  if (!d) return '';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  if (hours < 24) return remMin > 0 ? `${hours} h ${remMin} min` : `${hours} h`;
  const days = Math.floor(hours / 24);
  const remH = hours % 24;
  if (days < 30) return remH > 0 ? `${days} j ${remH} h` : `${days} j`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} mois`;
  const years = Math.floor(months / 12);
  return `${years} an${years > 1 ? 's' : ''}`;
}

/**
 * Renvoie une étiquette de statut prête à afficher selon l'état online.
 * Pour un poste offline avec une date `lastHeartbeat`, on retourne
 * `Hors ligne · 2 h 14 min`. Si pas de date, juste `Hors ligne`.
 */
export function fmtOnlineStatus(isOnline, lastHeartbeat) {
  if (isOnline) return 'En ligne';
  const since = fmtRelativeFromNow(lastHeartbeat);
  return since ? `Hors ligne · ${since}` : 'Hors ligne';
}

/**
 * Valeur de tri de la colonne « Statut ». À garder alignée sur
 * `fmtOnlineStatus` : on doit trier sur ce que l'étiquette montre.
 *
 * Trier sur le seul booléen `isOnline` (ou un 0/1) laisse toutes les machines
 * hors ligne à égalité — leur ordre devient celui du tableau d'origine, alors
 * que l'étiquette affiche une durée (« Hors ligne · 22 j »). On trie donc sur un
 * axe unique « dernier contact » :
 *   - en ligne   → +∞ (toujours regroupées à une extrémité)
 *   - hors ligne → horodatage du dernier heartbeat
 *   - jamais vue → 0 (hors ligne depuis le plus longtemps)
 *
 * Croissant : jamais vue, plus longtemps hors ligne … plus récemment vue, en ligne.
 */
export function onlineSortValue(isOnline, lastHeartbeat) {
  if (isOnline) return Number.MAX_SAFE_INTEGER;
  const t = lastHeartbeat ? new Date(lastHeartbeat).getTime() : NaN;
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Nom d'OS pour l'affichage en tableau.
 *
 * Sous Windows, `OsName` est la valeur WMI `Caption` — toujours préfixée de
 * « Microsoft » (« Microsoft Windows 11 Pro »). Le préfixe est identique sur
 * chaque ligne Windows : il n'apporte rien et pousse la partie utile hors de la
 * colonne, qui est tronquée. On l'élide à l'affichage uniquement — le tri et
 * l'infobulle gardent la valeur d'origine.
 */
export const fmtOsName = (osName) =>
  (osName || '').replace(/^Microsoft\s+/i, '').trim();

/**
 * Type de machine — « Serveur » ou « Poste de travail ».
 *
 * Le serveur normalise le champ à l'écriture (cf. `AgentService.NormalizeMachineType`) :
 * il n'y a que deux valeurs possibles, jamais de vide, donc pas de troisième cas
 * à afficher. Un `machineType` absent (réponse d'une API pas encore mise à jour)
 * retombe sur « poste de travail », comme le défaut de l'entité.
 */
export const isServerMachine = (machineType) =>
  (machineType || '').trim().toLowerCase() === 'server';

export const fmtMachineType = (machineType) =>
  isServerMachine(machineType) ? 'Serveur' : 'Poste de travail';

/**
 * Lien vers la fiche publique d'une vulnérabilité. Les identifiants stockés
 * sont des `CVE-AAAA-NNNN` (NVD) ; tout autre format (GHSA, USN…) renvoie à
 * OSV, qui les indexe tous, plutôt qu'à une page NVD qui répondrait 404.
 */
export const cveUrl = (id) => {
  const v = String(id || '').trim();
  return /^CVE-/i.test(v)
    ? `https://nvd.nist.gov/vuln/detail/${encodeURIComponent(v.toUpperCase())}`
    : `https://osv.dev/vulnerability/${encodeURIComponent(v)}`;
};
